import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { flexRender, type Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useMemo } from "react";
import { DEFAULT_OVERSCAN, TABLE_MIN_HEIGHT } from "./constants";
import { DesktopTableHeader } from "./DesktopTableHeader";
import { EmptyState } from "./EmptyState";
import type { DesktopTableViewProps } from "./types";
import { VirtualizedRow } from "./VirtualizedRow";

const getCellTitle = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;

  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return value.join(", ");
  }

  return undefined;
};

const DesktopBodyRow = React.memo(
  ({ row, height }: { row: Row<any>; height?: number }) => {
    return (
      <TableRow data-row-id={row.id} style={{ height }}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} title={getCellTitle(cell.getValue())}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    );
  },
);

(DesktopBodyRow as React.NamedExoticComponent<any>).displayName =
  "DesktopBodyRow";

export const DesktopTableView = <T extends object>(
  props: DesktopTableViewProps<T>,
): React.JSX.Element => {
  const {
    table,
    onRowClick,
    facetedColumnIds = [],
    filteredRows,
    estimatedRowHeight = 53,
    overscan = DEFAULT_OVERSCAN,
    virtualize = true,
    height,
    minHeightPx = TABLE_MIN_HEIGHT,
  } = props;

  const theme = useTheme();
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const rows = filteredRows ?? table.getRowModel().rows;

  const facetedSet = useMemo(
    () => new Set(facetedColumnIds),
    [facetedColumnIds],
  );

  const [dynamicHeight, setDynamicHeight] = React.useState<number>(minHeightPx);

  React.useLayoutEffect(() => {
    if (height != null) return;

    const el = tableContainerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const initialAvailable = window.innerHeight - rect.top;
    setDynamicHeight(Math.max(initialAvailable, minHeightPx));

    let timerId: ReturnType<typeof setTimeout> | null = null;

    const resizeObserver = new ResizeObserver((entries) => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(() => {
        for (const entry of entries) {
          const r = entry.target.getBoundingClientRect();
          const available = window.innerHeight - r.top;
          setDynamicHeight(Math.max(available, minHeightPx));
        }
      }, 100);
    });

    resizeObserver.observe(el);

    return () => {
      if (timerId) clearTimeout(timerId);
      resizeObserver.disconnect();
    };
  }, [height, minHeightPx]);

  const containerHeightSx =
    height != null ? { height } : { height: dynamicHeight };

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
    getItemKey: (index) => rows[index]?.id ?? index,
    initialRect: {
      width: 0,
      height: typeof height === "number" ? height : dynamicHeight,
    },
  });

  const virtualItems = virtualize ? rowVirtualizer.getVirtualItems() : [];
  const totalSize = virtualize ? rowVirtualizer.getTotalSize() : 0;

  const isClickable = !!onRowClick;

  const renderTableBody = () => {
    if (rows.length === 0) {
      return (
        <TableRow data-empty-state>
          <TableCell
            colSpan={table.getVisibleLeafColumns().length}
            sx={{ border: "none", p: 0, height: "100%" }}
          >
            <EmptyState height={(height != null ? 0 : dynamicHeight) - 120} />
          </TableCell>
        </TableRow>
      );
    }

    if (!virtualize) {
      return rows.map((row) => <DesktopBodyRow key={row.id} row={row} />);
    }

    // If virtualized but no items yet (e.g., measuring or display: none),
    // render a tiny slice to allow safe container measurement. Do NOT render the whole array.
    if (virtualItems.length === 0) {
      return rows
        .slice(0, 10)
        .map((row) => <DesktopBodyRow key={row.id} row={row} />);
    }

    return (
      <>
        <VirtualizedRow
          colSpan={table.getVisibleLeafColumns().length}
          overVisibleRows
          totalSize={totalSize}
          virtualItems={virtualItems}
        />
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          return (
            <DesktopBodyRow height={virtualRow.size} key={row.id} row={row} />
          );
        })}
        <VirtualizedRow
          colSpan={table.getVisibleLeafColumns().length}
          overVisibleRows={false}
          totalSize={totalSize}
          virtualItems={virtualItems}
        />
      </>
    );
  };

  const HEADER_HEIGHT = 53;

  return (
    <TableContainer
      ref={tableContainerRef}
      sx={{
        ...containerHeightSx,
        overflow: "auto",
        scrollbarGutter: "stable",
        minHeight: minHeightPx,
        "&::-webkit-scrollbar-track": {
          marginTop: `${HEADER_HEIGHT}px`,
        },
      }}
    >
      <MuiTable
        stickyHeader
        sx={{
          tableLayout: "fixed",
          width: "100%",
        }}
      >
        <TableHead>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => (
                <TableCell key={header.id} sx={{ width: header.getSize() }}>
                  <DesktopTableHeader
                    filterValue={header.column.getFilterValue()}
                    header={header}
                    isFilterableColumn={facetedSet.has(header.column.id)}
                    sortDirection={header.column.getIsSorted()}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody
          onClick={(e) => {
            if (!onRowClick) return;
            const tr = (e.target as HTMLElement).closest("tr");
            const rowId = tr?.getAttribute("data-row-id");

            if (rowId) {
              const row = table.getRow(rowId) as Row<T> | undefined;

              if (row) onRowClick(row.original);
            }
          }}
          sx={{
            "& td": {
              borderBottom: `1px solid ${theme.colors.secondary[1]}`,
              color: "text.primary",
              fontSize: 12,
              fontWeight: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            ...(isClickable && {
              "& .MuiTableRow-root:not([data-empty-state])": {
                cursor: "pointer",
                transition: "background-color 0.1s",
                "&:hover": {
                  "& td": {
                    backgroundColor: "#F2F2F7",
                  },
                  "& td:first-of-type": {
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  },
                  "& td:last-of-type": {
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                  },
                },
              },
            }),
            position: rows.length === 0 ? "relative" : undefined,
          }}
        >
          {renderTableBody()}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
